import { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import Head from 'next/head';
import Layout from '@/layouts';
import { AppSettings } from '@/shared/app-common';
import { getArticleDetailContent, getArticleRecommendTags, getNewArticleDetailData } from '@/lib/service';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { canonicalUrl, imgUrl } from '@/shared/utils';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { getTdkApi } from '@/lib/service/tdk';
import { blogDetailJsonLd, faqJsonLd } from '@/lib/jsonld';
import { removeLastPathSegment } from '@/shared/path-helper';
import { hasNoIndex } from '@/lib/noIndex';
const ArticleDetail = dynamic(() => import('@/components/ArticleDetail'), { ssr: true });

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

  const { params, locale, req, resolvedUrl } = context;
  const { id } = params;
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;

  const [articleDetailRes, localeRes, tdkRes] = await Promise.all([
    getNewArticleDetailData(`${id}`, locale),
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl.split('?')[0], locale),
  ]);

  if (articleDetailRes.code != 0) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }

  let detailContent: any = null;
  if (articleDetailRes?.data?.content_url != undefined) {
    detailContent = await getArticleDetailContent(articleDetailRes?.data?.content_url ?? '');
  }

  let recommendTags: any = null;
  if (articleDetailRes?.data?.article_id != undefined) {
    recommendTags = await getArticleRecommendTags({ currentId: `${articleDetailRes?.data?.article_id}` }, locale);
  }

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const parentPath = removeLastPathSegment(resolvedUrl);
  const parentUrl = siteUrl + parentPath;

  const getDesc = () => {
    if (tdkRes?.data?.description) {
      return tdkRes?.data?.description;
    }
    return detailContent?.data?.media_name != undefined
      ? tempData['head_Blog_Meta_Desc'].replace('x111x', `${detailContent?.data?.media_name}`)
      : detailContent?.data?.introduction || null;
  };

  const jsonld = blogDetailJsonLd(
    {
      title: tdkRes?.data?.title || detailContent?.data?.title || null,
      desc: getDesc(),
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      url: parentUrl,
    },
    { url: siteUrl }
  );

  // 生成FAQ JSON-LD（如果满足条件）
  let faqJsonLdData = null;
  const articleFaq = articleDetailRes?.data?.article_faq;
  if (
    articleFaq &&
    articleFaq.enable === true &&
    articleFaq.faqs &&
    Array.isArray(articleFaq.faqs) &&
    articleFaq.faqs.length > 0
  ) {
    // 过滤掉空的问答对
    const validFaqs = articleFaq.faqs.filter(
      (faq) => faq.question && faq.answer && faq.question.trim() !== '' && faq.answer.trim() !== ''
    );

    if (validFaqs.length > 0) {
      faqJsonLdData = faqJsonLd(
        {
          title: tdkRes?.data?.title || detailContent?.data?.title || null,
          desc: getDesc(),
          url: canonicalUrl(req.headers.host, resolvedUrl, locale),
        },
        { url: siteUrl },
        validFaqs.map((faq) => ({
          question: faq.question,
          answer: faq.answer,
        }))
      );
    }
  }

  const noIndex = hasNoIndex(id as string, locale);
  return {
    props: {
      ...localeRes,
      model: articleDetailRes?.data !== undefined ? { ...articleDetailRes?.data } : null,
      contentData: detailContent?.data !== undefined ? { ...detailContent?.data } : null,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      recommendTags: recommendTags?.data !== undefined ? recommendTags?.data : null,
      title: tdkRes?.data?.title || detailContent?.data?.title || null,
      description: getDesc(),
      jsonld,
      faqJsonLd: faqJsonLdData,
      noIndex,
    },
  };
};

const ArticleDetailPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  const getContent = useMemo(() => <ArticleDetail data={props} />, [props]);
  return <>{getContent}</>;
};

ArticleDetailPage.getLayout = function getLayout(page: React.ReactElement) {
  const contentData = page.props?.contentData;
  const noIndex = page.props?.noIndex;

  const title = page.props?.title;
  const desc = page.props?.description;
  const faqJsonLdData = page.props?.faqJsonLd;

  return (
    <Layout
      head={
        <Head>
          <title>{`${title} | ${AppSettings.name}`}</title>
          {/* <meta name="title" content={`${contentData?.title} | ${AppSettings.name}`} /> */}
          <meta name="description" content={desc} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={imgUrl(contentData?.image_url, 'mid')} />
          <meta property="og:image:secure_url" content={imgUrl(contentData?.image_url, 'mid')} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={imgUrl(contentData?.image_url, 'mid')} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          {noIndex && <meta name="robots" content="noindex" />}

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />

          {/* 文章详情的JSON-LD */}
          <script
            async
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonld) }}
          />

          {/* FAQ的JSON-LD（如果存在） */}
          {faqJsonLdData && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLdData) }}
            />
          )}

          {/* <script
            async
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(blogJsonData) }}
          /> */}

          <script
            async
            dangerouslySetInnerHTML={{
              __html: `
                (function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                })(window, document, "clarity", "script", "l9myh0u19b");
              `,
            }}
          />
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default ArticleDetailPage;
